import * as Yup from 'yup';
import { endpoints } from 'src/utils/axios';
import { HOST_API } from 'src/config-global';
import { designationConfig } from 'src/sections/designation-master/designationConfig';
import { blockConfig } from 'src/sections/block-master/blockConfig';
import { localityConfig } from 'src/sections/locality-master/localityConfig';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import PropTypes from 'prop-types';
import { useRights } from 'src/hooks/useRights';

const WorkDetailsMigrationAlert = ({ formData }) => {
  const rights = useRights('hrm_management', 'user_management', 'staff_migrations');
  const hasStaffMigrationRight = rights.canView;

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'info.light',
        bgcolor: 'info.lighter',
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ fontSize: 22, lineHeight: 1, mt: 0.25 }}>🔒</Box>
        <Box sx={{ flex: 1 }}>
          <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>Fields Locked for Edit</AlertTitle>
          <Box
            component="span"
            sx={{
              fontSize: 13,
              color: 'text.secondary',
              display: 'block',
              mb: hasStaffMigrationRight ? 1 : 0,
            }}
          >
            <strong>Designation</strong> and <strong>Reporting Manager</strong> cannot be changed
            directly. To update these, use the <strong>Staff Migration</strong> module which
            maintains a full audit trail.
          </Box>
          {hasStaffMigrationRight && (
            <Chip
              label="Go to Staff Migration →"
              size="small"
              color="info"
              variant="filled"
              onClick={() => {
                window.location.href = '/hrm_management/user_management/staff_migrations';
              }}
              sx={{ cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
            />
          )}
        </Box>
      </Box>
    </Alert>
  );
};

WorkDetailsMigrationAlert.propTypes = {
  formData: PropTypes.object,
};

const WorkLocationMigrationAlert = ({ formData }) => {
  const rights = useRights('hrm_management', 'user_management', 'staff_migrations');
  const hasStaffMigrationRight = rights.canView;

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'info.light',
        bgcolor: 'info.lighter',
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ fontSize: 22, lineHeight: 1, mt: 0.25 }}>🔒</Box>
        <Box sx={{ flex: 1 }}>
          <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>Fields Locked for Edit</AlertTitle>
          <Box
            component="span"
            sx={{
              fontSize: 13,
              color: 'text.secondary',
              display: 'block',
              mb: hasStaffMigrationRight ? 1 : 0,
            }}
          >
            <strong>Work Location</strong> (State, City, Blocks, Localities) cannot be changed
            directly. To update these, use the <strong>Staff Migration</strong> module which
            maintains a full audit trail.
          </Box>
          {hasStaffMigrationRight && (
            <Chip
              label="Go to Staff Migration →"
              size="small"
              color="info"
              variant="filled"
              onClick={() => {
                window.location.href = '/hrm_management/user_management/staff_migrations';
              }}
              sx={{ cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
            />
          )}
        </Box>
      </Box>
    </Alert>
  );
};

WorkLocationMigrationAlert.propTypes = {
  formData: PropTypes.object,
};

export const userConfig = {
  // Entity metadata
  entity: {
    name: 'User',
    namePlural: 'Users',
    apiEndpoint: endpoints.users, // Fixed: users (plural)
    storageKey: 'users-list-settings-v3', // Updated to v3 to show new DOB column
    rights: { module: 'hrm_management', menu: 'user_management', submenu: 'users' },
  },

  // Export configuration
  exportColumns: [
    { id: 'employeeId', label: 'Employee ID' },
    { id: 'firstName', label: 'First Name' },
    { id: 'lastName', label: 'Last Name' },
    { id: 'fatherOrHusbandName', label: "Father's/Husband's Name" },
    { id: 'email', label: 'Email' },
    { id: 'phone', label: 'Phone' },
    { id: 'whatsappNumber', label: 'WhatsApp Number' },
    { id: 'whatsappOptIn', label: 'WhatsApp Opt-In', render: (val) => (val ? 'Yes' : 'No') },
    {
      id: 'dateOfBirth',
      label: 'Date of Birth',
      render: (val) => {
        if (!val) return '-';
        const date = new Date(val);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      },
    },
    {
      id: 'joiningDate',
      label: 'Joining Date',
      render: (val) => {
        if (!val) return '-';
        const date = new Date(val);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      },
    },
    { id: 'designation', label: 'Designation', render: (val) => val?.designation_name || '-' },
    {
      id: 'reportingManager',
      label: 'Reporting Managers',
      render: (val) =>
        Array.isArray(val)
          ? val
              .map((m) =>
                `${m.firstName || ''} ${m.lastName || ''} (${m.employeeId || 'N/A'})`.trim()
              )
              .join(', ')
          : '-',
    },
    {
      id: 'status',
      label: 'Status',
      render: (val) => {
        const map = {
          active: 'Active',
          inactive: 'Inactive',
          suspended: 'Suspended',
          on_notice_period: 'On Notice Period',
        };
        return map[val] || val || '-';
      },
    },
    {
      id: 'positioningStates',
      label: 'Work Location - States',
      render: (val) => (Array.isArray(val) ? val.map((s) => s.state_name || '-').join(', ') : '-'),
    },
    {
      id: 'positioningCities',
      label: 'Work Location - Cities',
      render: (val) => (Array.isArray(val) ? val.map((c) => c.city_name || '-').join(', ') : '-'),
    },
    {
      id: 'positioningBlocks',
      label: 'Work Location - Blocks',
      render: (val) => (Array.isArray(val) ? val.map((b) => b.block_name || '-').join(', ') : '-'),
    },
    {
      id: 'positioningLocalities',
      label: 'Work Location - Localities',
      render: (val) =>
        Array.isArray(val) ? val.map((l) => l.locality_name || '-').join(', ') : '-',
    },
    { id: 'permanentAddress.street', label: 'Permanent Address - Street' },
    { id: 'permanentAddress.city', label: 'Permanent Address - City' },
    { id: 'permanentAddress.state', label: 'Permanent Address - State' },
    { id: 'permanentAddress.pincode', label: 'Permanent Address - Pincode' },
    { id: 'permanentAddress.country', label: 'Permanent Address - Country' },
    { id: 'residentAddress.street', label: 'Resident Address - Street' },
    { id: 'residentAddress.city', label: 'Resident Address - City' },
    { id: 'residentAddress.state', label: 'Resident Address - State' },
    { id: 'residentAddress.pincode', label: 'Resident Address - Pincode' },
    { id: 'residentAddress.country', label: 'Resident Address - Country' },
    { id: 'bankDetails.bankName', label: 'Bank Name' },
    { id: 'bankDetails.accountNumber', label: 'Account Number' },
    { id: 'bankDetails.ifscCode', label: 'IFSC Code' },
    { id: 'bankDetails.panNumber', label: 'PAN Card Number' },
    { id: 'bankDetails.pfAccountNumber', label: 'PF Number' },
    { id: 'medicalDetails.bloodGroup', label: 'Blood Group' },
    { id: 'medicalDetails.height', label: 'Height' },
    { id: 'medicalDetails.weight', label: 'Weight' },
    { id: 'medicalDetails.fitnessStatus', label: 'Fitness Status' },
    { id: 'medicalDetails.identificationMarks', label: 'Identification Marks' },
    { id: 'medicalDetails.allergiesOrConditions', label: 'Allergies / Conditions' },
    { id: 'medicalDetails.emergencyContactName', label: 'Emergency Contact Name' },
    { id: 'medicalDetails.emergencyContactRelation', label: 'Emergency Contact Relation' },
    { id: 'medicalDetails.emergencyContactPhone', label: 'Emergency Contact Phone' },
    { id: 'guardDetails.badgeNumber', label: 'Badge/UAN Number' },
    { id: 'guardDetails.guardType', label: 'Guard Type' },
    { id: 'guardDetails.previousExperienceYears', label: 'Prev. Experience (Years)' },
    { id: 'guardDetails.policeVerificationStatus', label: 'Police Verification Status' },
    {
      id: 'guardDetails.policeVerificationDate',
      label: 'Police Verification Date',
      render: (val) => {
        if (!val) return '-';
        const date = new Date(val);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      },
    },
    {
      id: 'guardDetails.trainingCompleted',
      label: 'Training Completed',
      render: (val) => (val ? 'Yes' : 'No'),
    },
    { id: 'guardDetails.gunLicenseNumber', label: 'Gun License Number' },
    {
      id: 'guardDetails.gunLicenseExpiry',
      label: 'Gun License Expiry Date',
      render: (val) => {
        if (!val) return '-';
        const date = new Date(val);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      },
    },
    {
      id: 'createdAt',
      label: 'Created At',
      render: (val) => {
        if (!val) return '-';
        return new Date(val).toLocaleDateString();
      },
    },
    {
      id: 'updatedAt',
      label: 'Updated At',
      render: (val) => {
        if (!val) return '-';
        return new Date(val).toLocaleDateString();
      },
    },
  ],

  // Data fetching configuration (optimized)
  fetch: {
    listFields: [
      'employeeId',
      'firstName',
      'lastName',
      'email',
      'phone',
      'designation',
      'reportingManager',
      'status',
      'dateOfBirth',
      'joiningDate',
      'createdAt',
      'updatedAt',
      'extraDetailsData',
    ],
    listPopulate: ['designation', 'reportingManager'],
  },

  // API endpoints
  endpoints: {
    list: endpoints.users.list, // Fixed: users (plural)
    create: endpoints.users.create,
    update: (id) => endpoints.users.update(id),
    delete: (id) => endpoints.users.delete(id),
    getById: (id) => endpoints.users.getById(id), // Fixed: use getById helper
  },

  // Form configuration
  form: {
    title: {
      create: 'Create New User',
      edit: 'Edit User',
    },
    maxWidth: 'lg',
    showAvatar: false,
    fields: [
      // Section 1: Basic Information
      {
        title: 'Basic Information',
        fields: [
          // Organization - Only for super_admin
          {
            name: 'organization',
            label: 'Organization',
            type: 'autocomplete',
            apiEndpoint: `${endpoints.organization.list}?status=active&limit=0&fields=minimal`,
            optionLabel: 'name',
            optionValue: '_id',
            validation: Yup.mixed().required('Organization is required'),
            gridProps: { xs: 12, md: 12 },
            // Only show for super_admin
            hidden: (formData, userRole) => userRole !== 'super_admin',
          },
          {
            name: 'firstName',
            label: 'First Name',
            type: 'text',
            placeholder: 'Enter first name',
            validation: Yup.string()
              .required('First name is required')
              .min(2, 'Minimum 2 characters'),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'lastName',
            label: 'Last Name',
            type: 'text',
            placeholder: 'Enter last name',
            validation: Yup.string()
              .required('Last name is required')
              .min(2, 'Minimum 2 characters'),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'fatherOrHusbandName',
            label: "Father's/Husband's Name",
            type: 'text',
            placeholder: "Enter father's or husband's name",
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'employeeId',
            label: 'Employee ID',
            type: 'text',
            placeholder: 'Enter employee ID',
            helperText: (formData, userRole, siteSettings) => {
              const isAuto = Boolean(
                siteSettings?.employeeIdSettings?.isAutoGenerate ||
                siteSettings?.employeeIdAutoGenerate
              );
              return isAuto
                ? 'Auto-generated based on site settings'
                : 'Manual entry required';
            },
            validation: Yup.string().when('$siteSettings', {
              is: (settings) => Boolean(
                settings?.employeeIdSettings?.isAutoGenerate ||
                settings?.employeeIdAutoGenerate
              ),
              then: (schema) => schema.notRequired(),
              otherwise: (schema) => schema.required('Employee ID is required'),
            }),
            gridProps: { xs: 12, sm: 6 },
            // Hide on creation when auto-generation is enabled
            hidden: (formData, userRole, siteSettings) => {
              const isAuto = Boolean(
                siteSettings?.employeeIdSettings?.isAutoGenerate ||
                siteSettings?.employeeIdAutoGenerate
              );
              const isEdit = Boolean(formData?._id || formData?.id);
              return isAuto && !isEdit;
            },
            disabled: (formData, userRole, siteSettings) => {
              const isEdit = Boolean(formData?._id || formData?.id);
              return isEdit;
            },
          },
          {
            name: 'dateOfBirth',
            label: 'Date of Birth',
            type: 'date',
            placeholder: 'Select date of birth',
            validation: Yup.date()
              .typeError('Please enter a valid date of birth')
              .required('Date of birth is required'),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'joiningDate',
            label: 'Joining Date',
            type: 'date',
            placeholder: 'Select joining date',
            validation: Yup.date()
              .typeError('Please enter a valid joining date')
              .required('Joining date is required'),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'email',
            label: 'Email',
            type: 'email',
            placeholder: 'Enter email address',
            validation: Yup.string().email('Invalid email').required('Email is required'),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'phone',
            label: 'Phone',
            type: 'text',
            placeholder: 'Enter phone number',
            validation: Yup.string()
              .matches(
                /^[0-9+\-\s()]*$/,
                'Phone number can only contain numbers, +, -, (), and spaces'
              )
              .min(10, 'Phone number must be at least 10 digits')
              .max(20, 'Phone number must not exceed 20 digits')
              .required('Phone is required'),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'whatsappNumber',
            label: 'WhatsApp Number',
            type: 'text',
            placeholder: 'Enter WhatsApp number',
            validation: Yup.string()
              .matches(
                /^[0-9+\-\s()]*$/,
                'WhatsApp number can only contain numbers, +, -, (), and spaces'
              )
              .min(10, 'WhatsApp number must be at least 10 digits')
              .max(20, 'WhatsApp number must not exceed 20 digits')
              .nullable()
              .notRequired(),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'whatsappOptIn',
            label: 'Opt-in for WhatsApp Notifications',
            type: 'switch',
            defaultValue: true,
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'password',
            label: 'Password',
            type: 'password',
            placeholder: 'Leave blank to keep current password',
            helperText: 'Only required when creating new user',
            validation: Yup.string().when('$isEdit', {
              is: true,
              then: (schema) =>
                schema
                  .transform((value) => (value === '' ? undefined : value))
                  .nullable()
                  .notRequired()
                  .test(
                    'password-strength',
                    'Password must be at least 8 characters and contain one uppercase, one lowercase, and one number',
                    (val) => {
                      if (!val) return true; // Optional if empty
                      return (
                        val.length >= 8 &&
                        val.length <= 100 &&
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(val)
                      );
                    }
                  ),
              otherwise: (schema) =>
                schema
                  .required('Password is required for new users')
                  .min(8, 'Password must be at least 8 characters')
                  .max(100, 'Password must not exceed 100 characters')
                  .matches(
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                    'Password must contain at least one uppercase letter, one lowercase letter, and one number'
                  ),
            }),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'requirePasswordChange',
            label: 'Require password change on first login',
            type: 'switch',
            defaultValue: false,
            helperText: 'User will be required to change password when they log in',
            gridProps: { xs: 12 },
          },
          {
            name: 'status',
            label: 'Status',
            type: 'select',
            defaultValue: 'active',
            validation: Yup.string().required('Status is required'),
            options: [
              { value: 'active', label: 'Active' },
              { value: 'on_notice_period', label: 'On Notice Period' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'suspended', label: 'Suspended' },
            ],
            gridProps: { xs: 12, sm: 6 },
          },
        ],
      },

      // Section 2: Work Details
      {
        title: 'Work Details',
        fields: [
          {
            name: 'workDetailsMigrationAlert',
            type: 'component',
            gridProps: { xs: 12 },
            render: ({ formData }) => {
              if (formData?._id || formData?.id) {
                return <WorkDetailsMigrationAlert formData={formData} />;
              }
              return null;
            },
          },
          {
            name: 'designation',
            label: 'Designation',
            type: 'autocomplete',
            apiEndpoint: `${endpoints.designationMaster.list}?status=active&limit=0`,
            optionLabel: 'designation_name',
            optionValue: '_id',
            placeholder: 'Select designation',
            required: true,
            validation: Yup.mixed().required('Designation is required'),
            gridProps: { xs: 12, sm: 6 },
            disabled: (formData) => !!(formData?._id || formData?.id),
            quickAddConfig: {
              formConfig: designationConfig,
              buttonLabel: 'Add New Designation',
              buttonIcon: 'mingcute:add-line',
            },
          },
          {
            name: 'reportingManager',
            label: 'Reporting Manager(s)',
            type: 'autocomplete',
            multiple: true,
            apiEndpoint: `${endpoints.users.list}?status=active&limit=0&fields=minimal`,
            optionLabel: (option) =>
              `${option.firstName || ''} ${option.lastName || ''} (${
                option.employeeId || 'N/A'
              })`.trim(),
            optionValue: '_id',
            placeholder: 'Select reporting manager(s)',
            required: true,
            validation: Yup.array().min(1, 'At least one reporting manager is required'),
            gridProps: { xs: 12, sm: 6 },
            disabled: (formData) => !!(formData?._id || formData?.id),
            helperText: (formData) =>
              formData?._id || formData?.id
                ? undefined
                : 'Select the direct supervisor(s) for this user',
          },
        ],
      },

      // Section 3: Work Location
      {
        title: 'Work Location',
        fields: [
          {
            name: 'workLocationMigrationAlert',
            type: 'component',
            gridProps: { xs: 12 },
            render: ({ formData }) => {
              if (formData?._id || formData?.id) {
                return <WorkLocationMigrationAlert formData={formData} />;
              }
              return null;
            },
          },
          {
            name: 'positioningStates',
            label: 'Work Location - State (Multi-select)',
            type: 'autocomplete',
            multiple: true,
            apiEndpoint: `${endpoints.stateMaster.list}?status=active&limit=0`,
            optionLabel: 'state_name',
            optionValue: '_id',
            placeholder: 'Select work location state(s)',
            required: true,
            validation: Yup.array().min(1, 'At least one work state is required'),
            gridProps: { xs: 12, sm: 6 },
            disabled: (formData) => !!(formData?._id || formData?.id),
            helperText: (formData) =>
              formData?._id || formData?.id ? undefined : 'State(s) where employee will work',
          },
          {
            name: 'positioningCities',
            label: 'Work Location - City (Multi-select)',
            type: 'autocomplete',
            multiple: true,
            apiEndpoint: (formData) => {
              const { positioningStates } = formData || {};
              if (!positioningStates || positioningStates.length === 0) return null;
              const stateIds = positioningStates
                .map((s) => s?._id || s?.id || s)
                .filter(Boolean)
                .join(',');
              return `${endpoints.cityMaster.list}?status=active&limit=0&state=${stateIds}`;
            },
            optionLabel: 'city_name',
            optionValue: '_id',
            placeholder: 'Select work location city(ies)',
            required: true,
            validation: Yup.array().min(1, 'At least one work city is required'),
            dependsOn: 'positioningStates',
            gridProps: { xs: 12, sm: 6 },
            disabled: (formData) => !!(formData?._id || formData?.id),
            helperText: (formData) =>
              formData?._id || formData?.id
                ? undefined
                : 'City(ies) where employee will work (select state first)',
          },
          {
            name: 'positioningBlocks',
            label: 'Work Location - Blocks (Multi-select)',
            type: 'autocomplete',
            multiple: true,
            apiEndpoint: (formData) => {
              const { positioningCities } = formData || {};
              if (!positioningCities || positioningCities.length === 0) return null;
              const cityIds = positioningCities
                .map((c) => c?._id || c?.id || c)
                .filter(Boolean)
                .join(',');
              return `${endpoints.blockMaster.list}?status=active&limit=0&city=${cityIds}`;
            },
            optionLabel: 'block_name',
            optionValue: '_id',
            placeholder: 'Select work location blocks',
            validation: Yup.array().nullable(),
            dependsOn: 'positioningCities',
            gridProps: { xs: 12 },
            disabled: (formData) => !!(formData?._id || formData?.id),
            helperText: (formData) =>
              formData?._id || formData?.id
                ? undefined
                : 'Select multiple blocks this user will oversee (select city first)',
            quickAddConfig: {
              formConfig: blockConfig,
              buttonLabel: 'Add New Block',
              buttonIcon: 'mingcute:add-line',
            },
          },
          {
            name: 'positioningLocalities',
            label: 'Work Location - Locality/Village (Multi-select)',
            type: 'autocomplete',
            multiple: true,
            apiEndpoint: (formData) => {
              const { positioningBlocks } = formData || {};
              if (!positioningBlocks || positioningBlocks.length === 0) return null;
              const blockIds = positioningBlocks
                .map((b) => b?._id || b?.id || b)
                .filter(Boolean)
                .join(',');
              return `${endpoints.localityMaster.list}?status=active&limit=0&block=${blockIds}`;
            },
            optionLabel: 'locality_name',
            optionValue: '_id',
            placeholder: 'Select work location localities/villages',
            validation: Yup.array().nullable(),
            dependsOn: 'positioningBlocks',
            gridProps: { xs: 12 },
            disabled: (formData) => !!(formData?._id || formData?.id),
            helperText: (formData) =>
              formData?._id || formData?.id
                ? undefined
                : 'Select multiple localities/villages this user will oversee (select block first)',
            quickAddConfig: {
              formConfig: localityConfig,
              buttonLabel: 'Add New Locality',
              buttonIcon: 'mingcute:add-line',
            },
          },
        ],
      },

      // Section 4: Address Details
      {
        title: 'Address Details',
        fields: [
          {
            name: 'permanentAddress.street',
            label: 'Permanent Address - Street',
            type: 'text',
            placeholder: 'Enter permanent street address',
            validation: Yup.string().required('Street address is required'),
            gridProps: { xs: 12 },
          },
          {
            name: 'permanentAddress.city',
            label: 'Permanent Address - City',
            type: 'text',
            placeholder: 'Enter permanent city',
            validation: Yup.string().required('City is required'),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'permanentAddress.state',
            label: 'Permanent Address - State',
            type: 'text',
            placeholder: 'Enter permanent state',
            validation: Yup.string().required('State is required'),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'permanentAddress.pincode',
            label: 'Permanent Address - Pincode',
            type: 'text',
            placeholder: 'Enter permanent pincode',
            validation: Yup.string()
              .required('Pincode is required')
              .test('is-pincode', 'Must be 6 digits', (value) => /^[0-9]{6}$/.test(value)),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'permanentAddress.country',
            label: 'Country',
            type: 'text',
            defaultValue: 'India',
            placeholder: 'Enter country',
            validation: Yup.string().required('Country is required'),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'sameAsPermanent',
            label: 'Resident Address same as Permanent Address',
            type: 'switch',
            gridProps: { xs: 12 },
          },
          {
            name: 'residentAddress.street',
            label: 'Resident Address - Street',
            type: 'text',
            placeholder: 'Enter resident street address',
            required: true,
            validation: Yup.string().required('Street address is required'),
            disabled: (formData) => formData?.sameAsPermanent === true,
            dependsOn: ['sameAsPermanent', 'permanentAddress.street'],
            computeValue: (triggerValue, formData) =>
              formData?.sameAsPermanent
                ? formData?.permanentAddress?.street || ''
                : formData?.residentAddress?.street || '',
            gridProps: { xs: 12 },
          },
          {
            name: 'residentAddress.city',
            label: 'Resident Address - City',
            type: 'text',
            placeholder: 'Enter resident city',
            required: true,
            validation: Yup.string().required('City is required'),
            disabled: (formData) => formData?.sameAsPermanent === true,
            dependsOn: ['sameAsPermanent', 'permanentAddress.city'],
            computeValue: (triggerValue, formData) =>
              formData?.sameAsPermanent
                ? formData?.permanentAddress?.city || ''
                : formData?.residentAddress?.city || '',
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'residentAddress.state',
            label: 'Resident Address - State',
            type: 'text',
            placeholder: 'Enter resident state',
            required: true,
            validation: Yup.string().required('State is required'),
            disabled: (formData) => formData?.sameAsPermanent === true,
            dependsOn: ['sameAsPermanent', 'permanentAddress.state'],
            computeValue: (triggerValue, formData) =>
              formData?.sameAsPermanent
                ? formData?.permanentAddress?.state || ''
                : formData?.residentAddress?.state || '',
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'residentAddress.pincode',
            label: 'Resident Address - Pincode',
            type: 'text',
            placeholder: 'Enter resident pincode',
            required: true,
            validation: Yup.string()
              .required('Pincode is required')
              .test('is-pincode', 'Must be 6 digits', (value) => /^[0-9]{6}$/.test(value)),
            disabled: (formData) => formData?.sameAsPermanent === true,
            dependsOn: ['sameAsPermanent', 'permanentAddress.pincode'],
            computeValue: (triggerValue, formData) =>
              formData?.sameAsPermanent
                ? formData?.permanentAddress?.pincode || ''
                : formData?.residentAddress?.pincode || '',
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'residentAddress.country',
            label: 'Country',
            type: 'text',
            defaultValue: 'India',
            placeholder: 'Enter country',
            disabled: (formData) => formData?.sameAsPermanent === true,
            dependsOn: ['sameAsPermanent', 'permanentAddress.country'],
            computeValue: (triggerValue, formData) =>
              formData?.sameAsPermanent
                ? formData?.permanentAddress?.country || 'India'
                : formData?.residentAddress?.country || 'India',
            gridProps: { xs: 12, sm: 6 },
          },
        ],
      },

      // Section 5: Bank Details
      {
        title: 'Bank Details',
        fields: [
          {
            name: 'bankDetails.accountNumber',
            label: 'Account Number',
            type: 'text',
            placeholder: 'Enter bank account number',
            validation: Yup.string().nullable().notRequired(),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'bankDetails.bankName',
            label: 'Bank Name',
            type: 'text',
            placeholder: 'Enter bank name',
            validation: Yup.string().nullable().notRequired(),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'bankDetails.ifscCode',
            label: 'IFSC Code',
            type: 'text',
            placeholder: 'Enter IFSC code',
            validation: Yup.string()
              .nullable()
              .notRequired()
              .test(
                'is-ifsc',
                'Invalid IFSC code (e.g., ABCD0123456)',
                (value) => !value || /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(value)
              ),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'bankDetails.panNumber',
            label: 'PAN Card Number',
            type: 'text',
            placeholder: 'Enter PAN card number (Optional)',
            validation: Yup.string()
              .nullable()
              .notRequired()
              .test(
                'is-pan',
                'Invalid PAN card number (e.g., ABCDE1234F)',
                (value) => !value || /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(value)
              ),
            gridProps: { xs: 12, sm: 6 },
          },
          {
            name: 'bankDetails.pfAccountNumber',
            label: 'PF Number',
            type: 'text',
            placeholder: 'Enter PF number',
            gridProps: { xs: 12, sm: 6 },
          },
        ],
      },
    ],
  },

  // Transform payload before submit
  transformPayload: (formData) => {
    // Step 1: Extract IDs from objects and handle address autofill
    const organizationValue =
      formData.organization?._id || formData.organization?.id || formData.organization;
    const residentAddress = formData.sameAsPermanent
      ? { ...formData.permanentAddress }
      : formData.residentAddress;

    const transformedData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      fatherOrHusbandName: formData.fatherOrHusbandName,
      employeeId: formData.employeeId,
      dateOfBirth: formData.dateOfBirth,
      joiningDate: formData.joiningDate,
      email: formData.email,
      phone: formData.phone,
      whatsappNumber: formData.whatsappNumber || '',
      whatsappOptIn: formData.whatsappOptIn || false,
      password: formData.password || undefined,
      designation: formData.designation?._id || formData.designation || null,
      reportingManager: Array.isArray(formData.reportingManager)
        ? formData.reportingManager.map((m) => m._id || m)
        : [],
      status: formData.status || 'active',
      positioningStates: Array.isArray(formData.positioningStates)
        ? formData.positioningStates.map((s) => s._id || s.id || s)
        : [],
      positioningCities: Array.isArray(formData.positioningCities)
        ? formData.positioningCities.map((c) => c._id || c.id || c)
        : [],
      positioningBlocks: Array.isArray(formData.positioningBlocks)
        ? formData.positioningBlocks.map((b) => b._id || b.id || b)
        : [],
      positioningLocalities: Array.isArray(formData.positioningLocalities)
        ? formData.positioningLocalities.map((l) => l._id || l.id || l)
        : [],
      permanentAddress: formData.permanentAddress,
      residentAddress,
      sameAsPermanent: formData.sameAsPermanent || false,
      bankDetails: {
        ...formData.bankDetails,
        ifscCode: formData.bankDetails?.ifscCode?.toUpperCase() || '',
        panNumber: formData.bankDetails?.panNumber?.toUpperCase() || '',
        pfAccountNumber: formData.bankDetails?.pfAccountNumber || '',
      },
      organization: organizationValue || undefined,
      requirePasswordChange: formData.requirePasswordChange,
      extraDetailsData: formData.extraDetailsData || {},
    };

    return transformedData;
  },

  // Normalize data for form (when editing)
  normalizeDataForForm: (data) => {
    if (!data)
      return {
        organization: null, // For super_admin
        firstName: '',
        lastName: '',
        fatherOrHusbandName: '',
        employeeId: '',
        dateOfBirth: null,
        joiningDate: null,
        email: '',
        phone: '',
        whatsappNumber: '',
        whatsappOptIn: true,
        password: '',
        designation: null,
        reportingManager: [],
        status: 'active',
        positioningStates: [],
        positioningCities: [],
        positioningBlocks: [],
        positioningLocalities: [],
        permanentAddress: {
          street: '',
          city: '',
          state: '',
          pincode: '',
          country: 'India',
        },
        residentAddress: {
          street: '',
          city: '',
          state: '',
          pincode: '',
          country: 'India',
        },
        sameAsPermanent: false,
        bankDetails: {
          accountNumber: '',
          bankName: '',
          ifscCode: '',
          panNumber: '',
          pfAccountNumber: '',
        },
        extraDetailsData: {},
      };

    // Auto-fill resident address if sameAsPermanent
    let residentAddress = data.residentAddress || {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
    };
    if (data.sameAsPermanent && data.permanentAddress) {
      residentAddress = { ...data.permanentAddress };
    }

    return {
      _id: data._id || data.id || null,
      id: data.id || data._id || null,
      organization:
        typeof data.organization === 'object' && data.organization?._id ? data.organization : null,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      fatherOrHusbandName: data.fatherOrHusbandName || '',
      employeeId: data.employeeId || '',
      dateOfBirth: (() => {
        if (!data.dateOfBirth) return null;
        const d = new Date(data.dateOfBirth);
        return !Number.isNaN(d.getTime()) ? d : null;
      })(),
      joiningDate: (() => {
        if (!data.joiningDate) return null;
        const d = new Date(data.joiningDate);
        return !Number.isNaN(d.getTime()) ? d : null;
      })(),
      email: data.email || '',
      phone: data.phone || '',
      whatsappNumber: data.whatsappNumber || '',
      whatsappOptIn: data.whatsappOptIn !== undefined ? data.whatsappOptIn : true,
      password: '', // Never populate password
      designation:
        typeof data.designation === 'object' && data.designation?._id ? data.designation : null,
      reportingManager: Array.isArray(data.reportingManager)
        ? data.reportingManager.filter((m) => typeof m === 'object' && m?._id)
        : [],
      status: data.status || 'active',
      // Normalize positioningStates — fall back to singular legacy field for users
      // created before the multi-location feature (their plural arrays are empty)
      positioningStates: (() => {
        const plural = Array.isArray(data.positioningStates)
          ? data.positioningStates.filter((s) => typeof s === 'object' && s?._id)
          : [];
        if (plural.length > 0) return plural;
        // Fallback: legacy singular field
        if (
          data.positioningState &&
          typeof data.positioningState === 'object' &&
          data.positioningState._id
        ) {
          return [data.positioningState];
        }
        return [];
      })(),
      // Normalize positioningCities — same fallback pattern
      positioningCities: (() => {
        const plural = Array.isArray(data.positioningCities)
          ? data.positioningCities.filter((c) => typeof c === 'object' && c?._id)
          : [];
        if (plural.length > 0) return plural;
        // Fallback: legacy singular field
        if (
          data.positioningCity &&
          typeof data.positioningCity === 'object' &&
          data.positioningCity._id
        ) {
          return [data.positioningCity];
        }
        return [];
      })(),
      positioningBlocks: Array.isArray(data.positioningBlocks)
        ? data.positioningBlocks.filter((b) => typeof b === 'object' && b?._id)
        : [],
      positioningLocalities: Array.isArray(data.positioningLocalities)
        ? data.positioningLocalities.filter((l) => typeof l === 'object' && l?._id)
        : [],
      permanentAddress: {
        street: data.permanentAddress?.street || '',
        city: data.permanentAddress?.city || '',
        state: data.permanentAddress?.state || '',
        pincode: data.permanentAddress?.pincode || '',
        country: data.permanentAddress?.country || 'India',
      },
      residentAddress: {
        street: residentAddress.street || '',
        city: residentAddress.city || '',
        state: residentAddress.state || '',
        pincode: residentAddress.pincode || '',
        country: residentAddress.country || 'India',
      },
      sameAsPermanent: data.sameAsPermanent || false,
      bankDetails: {
        accountNumber: data.bankDetails?.accountNumber || '',
        bankName: data.bankDetails?.bankName || '',
        ifscCode: data.bankDetails?.ifscCode || '',
        panNumber: data.bankDetails?.panNumber || '',
        pfAccountNumber: data.bankDetails?.pfAccountNumber || '',
      },
      extraDetailsData: data.extraDetailsData || {},
    };
  },

  // Table configuration
  table: {
    columns: [
      {
        id: 'employeeId',
        label: 'Employee ID',
        width: 120,
        sortable: true,
        searchable: true,
      },
      {
        id: 'firstName',
        label: 'Name',
        width: 200,
        sortable: true,
        searchable: true,
        render: (value, row) => `${row.firstName} ${row.lastName}`,
      },
      {
        id: 'email',
        label: 'Email',
        width: 200,
        sortable: true,
        searchable: true,
        render: (value) => value,
      },
      {
        id: 'phone',
        label: 'Phone',
        width: 120,
      },
      {
        id: 'designation',
        label: 'Designation',
        width: 150,
        render: (value) => value?.designation_name || '-',
      },
      {
        id: 'reportingManager',
        label: 'Reporting Manager(s)',
        width: 200,
        render: (val) => {
          if (Array.isArray(val) && val.length > 0) {
            return val.map((m) => `${m.firstName || ''} ${m.lastName || ''}`.trim()).join(', ');
          }
          return '-';
        },
      },
      {
        id: 'dateOfBirth',
        label: 'Date of Birth',
        width: 130,
        type: 'date',
        sortable: true,
        render: (value) => {
          if (!value) return '-';
          const date = new Date(value);
          const day = date.getDate();
          const month = date.getMonth() + 1;
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        },
      },
      {
        id: 'status',
        label: 'Status',
        width: 120,
        type: 'status',
        sortable: true,
        align: 'center',
        render: (value) => {
          const statusMap = {
            active: { label: 'Active', color: 'success' },
            inactive: { label: 'Inactive', color: 'default' },
            suspended: { label: 'Suspended', color: 'error' },
            on_notice_period: { label: 'On Notice', color: 'warning' },
          };
          return statusMap[value] || { label: value, color: 'default' };
        },
      },
      {
        id: 'joiningDate',
        label: 'Joining Date',
        width: 120,
        type: 'date',
        sortable: true,
        render: (value) => {
          if (!value) return '-';
          const date = new Date(value);
          const day = date.getDate();
          const month = date.getMonth() + 1;
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        },
      },
      {
        id: 'documents',
        label: 'Documents',
        width: 130,
        sortable: false,
        render: (value, row) => {
          if (!row.documents) return '0/5 Uploaded';
          const required = ['aadhaarCard', 'panCard', 'bankPassbook', 'passport', 'photo'];
          const uploadedCount = required.filter((key) => row.documents[key]).length;
          return `${uploadedCount}/${required.length} Uploaded`;
        },
      },
    ],
    defaultSort: { field: 'firstName', direction: 'asc' },
    rowsPerPageOptions: [10, 25, 50, 100],
    defaultRowsPerPage: 10,
  },

  // Filter configuration
  filters: {
    default: {
      search: '',
      status: 'all',
      designation: null,
    },
    fields: [
      {
        name: 'search',
        label: 'Search',
        type: 'search',
        placeholder: 'Search users...',
        apiParam: 'search',
      },
      {
        name: 'status',
        label: 'Status',
        type: 'tabs',
        options: [
          { value: 'all', label: 'All', color: 'default' },
          { value: 'active', label: 'Active', color: 'success' },
          { value: 'on_notice_period', label: 'On Notice', color: 'warning' },
          { value: 'inactive', label: 'Inactive', color: 'default' },
          { value: 'suspended', label: 'Suspended', color: 'error' },
        ],
        apiParam: 'status',
        showCount: true,
      },
      {
        name: 'designation',
        label: 'Designation',
        type: 'autocomplete',
        apiEndpoint: `${endpoints.designationMaster.list}?status=active&limit=0`,
        optionLabel: 'designation_name',
        optionValue: '_id',
        apiParam: 'designation',
        inAdvancedFilters: true,
      },
    ],
  },

  // Card view configuration
  card: {
    title: (item) => `${item.firstName} ${item.lastName}`,
    subtitle: (item) => item.employeeId,
    avatar: (item) => (item.documents?.photo ? `${HOST_API}/${item.documents.photo}` : null),
    status: (item) => {
      const statusMap = {
        active: { label: 'Active', color: 'success' },
        inactive: { label: 'Inactive', color: 'default' },
        suspended: { label: 'Suspended', color: 'error' },
        on_notice_period: { label: 'On Notice', color: 'warning' },
      };
      return statusMap[item.status] || { label: item.status, color: 'default' };
    },
    metadata: [
      {
        label: 'Email',
        value: (item) => item.email,
        icon: 'solar:letter-bold-duotone',
      },
      {
        label: 'Phone',
        value: (item) => item.phone,
        icon: 'solar:phone-bold-duotone',
      },
      {
        label: 'Designation',
        value: (item) => item.designation?.designation_name || '-',
        icon: 'solar:user-id-bold-duotone',
      },
      {
        label: 'Joining Date',
        value: (item) => new Date(item.joiningDate).toLocaleDateString(),
        icon: 'solar:calendar-bold-duotone',
      },
    ],
  },

  // Permissions & features
  features: {
    create: true,
    edit: true,
    delete: true,
    bulkDelete: true,
    export: true,
    import: true,
    duplicate: true,
    viewToggle: true,
    columnVisibility: true,
    customActions: [
      {
        label: 'Documents',
        icon: 'solar:folder-with-files-bold-duotone',
        action: 'upload_documents',
      },
      {
        label: 'Change Password',
        icon: 'solar:key-bold-duotone',
        action: 'change_password',
      },
    ],
  },
};
